import { QueueMetric } from 'lib-dashboard-types';
import { useCallback, useEffect, useRef } from 'react';
import { useEffectOnce, useMap, useUpdateEffect } from 'react-use';

import { mergeDashboardMetric } from 'apis/dashboard';
import { WebSocketRealTimeQueueDashboardMetricsChangedInboundMessage } from 'middleware/webSocket';
import { pubSub } from 'utils/pubSub';

import { convertMetricsToMap } from './utils/convertMetricsToMap';

type UseQueueDashboardMetricsMapOptions = {
  /** The list of queue metrics. */
  metrics?: readonly QueueMetric[];
  /**
   * The optional queue the metrics are associated with. If not provided, the
   * metric is a global aggregate metric of all queues.
   */
  queueId?: string;
};

type UseQueueDashboardMetricsMapResult = ReturnType<
  typeof useMap<ReturnType<typeof convertMetricsToMap>>
>;

/**
 * A hook that given a list of queue metrics, it transforms it to a map and
 * handles updates through the WebSocket connection, ensuring that only metrics
 * that change are updated in the map and therefore re-rendered in the UI.
 */
export const useQueueDashboardMetricsMap = ({
  queueId,
  metrics,
}: UseQueueDashboardMetricsMapOptions): UseQueueDashboardMetricsMapResult => {
  const map = useMap(convertMetricsToMap(metrics));
  const [_, { setAll, set, get }] = map;

  /**
   * Handles partially updating individual metrics, when changes come in through
   * the WebSocket connection.
   */
  const handleMetricsChanged = useCallback(
    (payload: WebSocketRealTimeQueueDashboardMetricsChangedInboundMessage) => {
      if (!queueId) return;

      const updatedQueueMetrics = payload.params.queue_metrics.find(
        ({ id }) => id === queueId,
      );

      if (updatedQueueMetrics) {
        for (const metric of updatedQueueMetrics.metrics) {
          set(metric.type, mergeDashboardMetric(get(metric.type), metric));
        }
      }
    },
    [get, queueId, set],
  );

  /**
   * A ref to the `handleMetricsChanged` function, which ensures that the latest
   * version of the function is used when calling the subscription callback.
   */
  const handleMetricsChangedRef = useRef<typeof handleMetricsChanged | null>(
    null,
  );
  useEffect(() => {
    handleMetricsChangedRef.current = handleMetricsChanged;
  }, [handleMetricsChanged]);

  // Handles subscribing to the queue metrics change event.
  useEffectOnce(() => {
    if (!queueId) return;

    const unsubscribe = pubSub.subscribe(
      'real_time_dashboard:queue_metrics_changed',
      (payload) => {
        handleMetricsChangedRef.current?.(payload);
      },
    );

    return () => unsubscribe();
  });

  // Handles updating the map when the source metrics list changes.
  useUpdateEffect(() => {
    if (metrics) {
      setAll(convertMetricsToMap(metrics));
    }
  }, [metrics]);

  return map;
};
